import { forwardRef, useId } from 'react';

const baseField =
  'w-full min-h-touch rounded-2xl border bg-surface px-4 py-3 text-base text-ink placeholder:text-ink-faint ' +
  // Soft resting depth + a clear focus ring (colour set per error/valid state
  // below via ring-* on focus) with a smooth ~150ms settle.
  'shadow-soft transition-[border-color,box-shadow] duration-150 ' +
  'focus:outline-none focus:ring-2 focus:ring-offset-0 ' +
  'disabled:bg-oat/40 disabled:text-ink-faint disabled:shadow-none';

/**
 * Font size stays at 16px (text-base): anything smaller makes iOS Safari zoom
 * the viewport on focus, which visibly breaks the layout mid-form.
 */
const Input = forwardRef(function Input(
  { label, hint, error, id, className = '', containerClassName = '', as = 'input', prefix, children, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  const Tag = as;

  return (
    <div className={`flex flex-col gap-1.5 ${containerClassName}`}>
      {label && (
        <label htmlFor={fieldId} className="text-sm font-semibold text-heading">
          {label}
        </label>
      )}

      {/* A prefix (e.g. "+91") sits inside the field and is centred against the
          field itself — inset-y-0 rather than a fixed offset, so it stays put
          however tall the label, hint or error around it happen to be. */}
      <div className={prefix ? 'relative' : undefined}>
        {prefix && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-base text-ink-muted"
          >
            {prefix}
          </span>
        )}
        <Tag
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            baseField,
            error
              ? 'border-persimmon focus:border-persimmon focus:ring-persimmon/30'
              : 'border-oat-dark focus:border-leaf-600 focus:ring-leaf-600/25',
            as === 'textarea' ? 'min-h-[6rem] resize-y' : '',
            as === 'select' ? 'appearance-none pr-10' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...props}
        >
          {children}
        </Tag>
      </div>

      {error ? (
        <p id={`${fieldId}-error`} className="text-sm font-medium text-persimmon-dark">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export const Textarea = forwardRef(function Textarea(props, ref) {
  return <Input as="textarea" ref={ref} {...props} />;
});

export const Select = forwardRef(function Select({ children, ...props }, ref) {
  return (
    <div className="relative">
      <Input as="select" ref={ref} {...props}>
        {children}
      </Input>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-4 top-[2.55rem] text-ink-muted"
      >
        ▾
      </span>
    </div>
  );
});

export default Input;
